<!--
  navItem "Konten & Salden" (action `account`) — the account statement of ONE agent on ONE account.

  SIGN RULE (design gate FD-1 / B1): every colour and every figure below reads the SIGN OF THE
  DELIVERED `signedAmount` / `runningBalance`. `bookingKind` appears exactly once, as the German
  caption of the "Richtung" column; it is never turned into a colour, a sign or an operand.

  ENUM RULE: cp enums travel as ORDINALS, so PROVISION_ACCOUNT / CALCULATED / CREDIT are 0 and
  falsy. Every check compares explicitly instead of testing truthiness. Their German names come from
  the label companions in `enum/provision-*-label.ts`, reached through the thin forwarding methods
  of the component — an enum constant the frontend does not know yet renders VISIBLY, never as a
  blank cell.

  BRAKE RULE: the red "Auszahlungen werden gebremst" banner is painted from `reserveUnderfunded`
  alone — the flag the statement response carries from ProvisionPayoutService. The simulated
  shortfall is a FORECAST and gets its own calm, differently worded notice.

  PAGING: "Weitere Buchungen laden" appends the NEXT page over the exclusive cursor
  `nextAnchorTransactionId` (see the paging block near the end). There is no page-size number and no
  reach ceiling in this screen — `null` cursor is the module's end-of-journal signal and the only
  stopping point.

  Layout exclusively via PrimeFlex, controls exclusively via PrimeNG, no hand-written form HTML —
  the two write actions open the existing metadata-driven dialogs at the end of the file.
-->
<div class="cp-provision-agent-account">

    @if (!initialized) {

        <p-skeleton height="9rem" styleClass="mb-3"></p-skeleton>
        <p-skeleton height="20rem"></p-skeleton>

    } @else if (!provisionAgentId) {

        <p-message severity="info" text="Kein Abrechnungskonto ausgewählt." styleClass="w-full"></p-message>

    } @else {

        <!-- ══════════════════════════════════════════════════════════════════════════════════ -->
        <!-- RESERVE NOTICES — three states, and only the FIRST one is the payout brake          -->
        <!--                                                                                    -->
        <!-- 1. RED = the brake. It comes EXCLUSIVELY from `reserveUnderfunded`, which the       -->
        <!--    statement response carries from ProvisionPayoutService — the service that        -->
        <!--    actually stops payouts. Nothing else may ever paint this banner.                 -->
        <!-- 2. BLUE = a FORECAST on the simulated balance, i.e. on bookings that are calculated -->
        <!--    but NOT YET ACTIVATED. It is deliberately not red, not called "Unterdeckung" and -->
        <!--    says in its own words that no brake is active — two banners both claiming an     -->
        <!--    underfunding would leave the operator unable to tell which one stops a payout.   -->
        <!-- 3. AMBER = the balance row could not be read, so the forecast is missing.           -->
        <!--                                                                                    -->
        <!-- All three stay visible on the Provisionskonto too (decision D3).                    -->
        <!-- ══════════════════════════════════════════════════════════════════════════════════ -->
        @if (reserveUnderfunded) {
            <p-message severity="error" styleClass="w-full mb-3">
                <span>
                    <i class="fa-regular fa-triangle-exclamation mr-2"></i>
                    <strong>Unterdeckung des Rücklagenkontos.</strong>
                    Auszahlungen an diesen Berater werden dadurch gebremst.
                    <!-- The amount comes from the separately loaded balance row, the brake from the
                         statement — so the figure may be missing while the brake is stated. The
                         sentence above carries the message on its own; the number only details it. -->
                    @if (balanceDto?.reserveBalance != null) {
                        Der Rücklagensaldo beträgt
                        {{ balanceDto.reserveBalance | currency:'EUR':'symbol':'1.2-2' }}.
                    }
                </span>
            </p-message>
        } @else if (reserveSimulatedForecastNegative) {
            <p-message severity="info" styleClass="w-full mb-3">
                <span>
                    <i class="fa-regular fa-chart-line mr-2"></i>
                    <strong>Vorschau: Das Rücklagenkonto läuft ins Minus.</strong>
                    Der simulierte Rücklagensaldo ist negativ
                    ({{ balanceDto?.reserveSimulatedBalance | currency:'EUR':'symbol':'1.2-2' }}) —
                    berechnete, aber noch nicht aktivierte Buchungen führen dorthin. Das ist eine
                    Prognose und keine aktive Auszahlungsbremse: gebremst wird erst, wenn der reale
                    Rücklagensaldo unterdeckt ist.
                </span>
            </p-message>
        } @else if (balanceLoadFailed) {
            <!-- The third state: the balance row could not be read. Silence here would render an
                 unknown reserve exactly like a healthy one — the unsafe direction. The BRAKE above
                 is unaffected; it travels with the statement. -->
            <p-message severity="warn" styleClass="w-full mb-3">
                <span>
                    <i class="fa-regular fa-circle-question mr-2"></i>
                    <strong>Rücklagenstand unbekannt.</strong>
                    Der Rücklagenstand konnte nicht geladen werden — eine drohende Unterdeckung ist
                    hier nicht sichtbar.
                </span>
            </p-message>
        }

        <!-- ══════════════════════════════════════════════════════════════════════════════════ -->
        <!-- ACCOUNT SWITCH + BALANCE HEAD (decision D2)                                        -->
        <!-- Real balance large and coloured by ITS OWN sign, simulated balance as the          -->
        <!-- secondary figure beside it. No real/simulated toggle — both are visible at once.   -->
        <!-- ══════════════════════════════════════════════════════════════════════════════════ -->
        <div class="surface-card border-round p-3 mb-3">

            <div class="flex flex-wrap align-items-center justify-content-between gap-3">

                <p-selectButton
                        [options]="accountKindOptions"
                        [(ngModel)]="accountKind"
                        optionLabel="label"
                        optionValue="value"
                        [allowEmpty]="false"
                        (onChange)="handleAccountKindChange()">
                </p-selectButton>

                <div class="flex flex-wrap align-items-center gap-2">
                    <p-button
                            [icon]="direction === 'asc' ? 'fa-regular fa-arrow-down-short-wide' : 'fa-regular fa-arrow-down-wide-short'"
                            [label]="direction === 'asc' ? 'Älteste zuerst' : 'Neueste zuerst'"
                            severity="secondary"
                            [text]="true"
                            pTooltip="Sortierreihenfolge der Buchungen umschalten"
                            tooltipPosition="top"
                            (onClick)="toggleSortDirection()">
                    </p-button>

                    <p-button
                            label="Manuelle Buchung"
                            icon="fa-regular fa-pen-to-square"
                            severity="primary"
                            [disabled]="!canManualBooking"
                            [pTooltip]="manualBookingTooltip"
                            tooltipPosition="top"
                            (onClick)="openManualBookingDialog()">
                    </p-button>

                    <p-button
                            label="Umbuchung"
                            icon="fa-regular fa-right-left"
                            severity="secondary"
                            [outlined]="true"
                            [disabled]="!canReserveTransfer"
                            [pTooltip]="reserveTransferTooltip"
                            tooltipPosition="top"
                            (onClick)="openTransferDialog()">
                    </p-button>
                </div>

            </div>

            <div class="flex flex-wrap align-items-end gap-4 mt-3">

                <!-- Real balance — the leading figure.
                     This block sits OUTSIDE the busy/error branches below and keeps rendering when
                     the statement call failed. It must therefore never print a figure it does not
                     have: an unknown balance is "—", not a green 0,00 €. Exactly 0 is neutral too
                     (amountToneClass) — a settled account is not an account in credit. -->
                <div class="flex flex-column">
                    <span class="text-color-secondary text-sm mb-1">
                        {{ accountKindLabel }} — realer Saldo
                    </span>
                    <span class="text-3xl font-bold" [ngClass]="amountToneClass(currentBalance)">
                        @if (currentBalance != null) {
                            {{ currentBalance | currency:'EUR':'symbol':'1.2-2' }}
                        } @else {
                            —
                        }
                    </span>
                    @if (currentBalance != null) {
                        <span class="text-color-secondary text-xs mt-1">nur aktive Buchungen</span>
                    } @else {
                        <span class="text-color-secondary text-xs mt-1">nicht geladen</span>
                    }
                </div>

                <!-- Simulated balance — the secondary figure, side by side (D2). -->
                <div class="flex flex-column">
                    <span class="text-color-secondary text-sm mb-1">simulierter Saldo</span>
                    <span class="text-xl font-semibold" [ngClass]="amountToneClass(simulatedBalance)">
                        @if (simulatedBalance != null) {
                            {{ simulatedBalance | currency:'EUR':'symbol':'1.2-2' }}
                        } @else {
                            —
                        }
                    </span>
                    @if (simulatedBalance != null) {
                        <span class="text-color-secondary text-xs mt-1">inklusive berechneter Buchungen</span>
                    } @else {
                        <span class="text-color-secondary text-xs mt-1">nicht geladen</span>
                    }
                </div>

                <div class="flex flex-column ml-auto text-right">
                    <span class="text-color-secondary text-sm">
                        <i class="fa-regular fa-clock mr-1"></i>
                        Zuletzt berechnet:
                        @if (lastCalculatedDate) {
                            {{ lastCalculatedDate | date:'dd.MM.yyyy HH:mm' }}
                        } @else {
                            noch nie
                        }
                    </span>
                    @if (totalTransactionCount != null) {
                        <span class="text-color-secondary text-sm mt-1">
                            {{ totalTransactionCount }} Buchungen im Filter
                        </span>
                    }
                    @if (jobRunning) {
                        <span class="text-color-secondary text-xs mt-1">
                            <i class="fa-regular fa-spinner fa-spin mr-1"></i>
                            Ein Provisionslauf arbeitet gerade — die Zahlen sind ein Zwischenstand.
                        </span>
                    }
                </div>

            </div>

        </div>

        <!-- ══════════════════════════════════════════════════════════════════════════════════ -->
        <!-- FILTER BAR — view controls only, no business data is captured here                 -->
        <!-- ══════════════════════════════════════════════════════════════════════════════════ -->
        <div class="surface-card border-round p-3 mb-3">
            <div class="flex flex-wrap align-items-end gap-3">

                <!-- (onSelect) fires only for a date PICKED in the overlay; a hand-typed date would
                     otherwise never reach the filter. The model change is therefore bound as well
                     and debounced in the component, because the picker parses per keystroke. -->
                <div class="flex flex-column gap-1">
                    <label class="text-color-secondary text-sm">Valuta von</label>
                    <p-datePicker
                            [ngModel]="filterValutaFrom"
                            (ngModelChange)="handleValutaFromInput($event)"
                            dateFormat="dd.mm.yy"
                            [showIcon]="true"
                            [showClear]="true"
                            styleClass="w-11rem"
                            placeholder="Datum"
                            (onSelect)="applyValutaFilterNow()"
                            (onClear)="applyValutaFilterNow()">
                    </p-datePicker>
                </div>

                <div class="flex flex-column gap-1">
                    <label class="text-color-secondary text-sm">Valuta bis</label>
                    <p-datePicker
                            [ngModel]="filterValutaTo"
                            (ngModelChange)="handleValutaToInput($event)"
                            dateFormat="dd.mm.yy"
                            [showIcon]="true"
                            [showClear]="true"
                            styleClass="w-11rem"
                            placeholder="Datum"
                            (onSelect)="applyValutaFilterNow()"
                            (onClear)="applyValutaFilterNow()">
                    </p-datePicker>
                </div>

                <div class="flex align-items-center gap-2">
                    <p-checkbox
                            inputId="cpAccountIncludeCalculated"
                            [(ngModel)]="includeCalculated"
                            [binary]="true"
                            (onChange)="handleFilterChange()">
                    </p-checkbox>
                    <label for="cpAccountIncludeCalculated"
                           class="text-sm cursor-pointer"
                           pTooltip="Bezieht berechnete, noch nicht aktivierte Buchungen ein. Die Saldospalte folgt dann dem simulierten Saldo."
                           tooltipPosition="top">
                        Berechnete Buchungen einbeziehen
                    </label>
                </div>

                <div class="flex align-items-center gap-2">
                    <p-checkbox
                            inputId="cpAccountIncludeCancelled"
                            [(ngModel)]="includeCancelled"
                            [binary]="true"
                            (onChange)="handleFilterChange()">
                    </p-checkbox>
                    <label for="cpAccountIncludeCancelled"
                           class="text-sm cursor-pointer"
                           pTooltip="Verworfene Buchungen bleiben saldoneutral und werden durchgestrichen dargestellt."
                           tooltipPosition="top">
                        Verworfene Buchungen anzeigen
                    </label>
                </div>

                <p-button
                        label="Filter zurücksetzen"
                        icon="fa-regular fa-filter-circle-xmark"
                        severity="secondary"
                        [text]="true"
                        (onClick)="handleFilterReset()">
                </p-button>

            </div>
        </div>

        <!-- The two actions are open by default (optimistic); this banner appears only after the
             SERVER refused one of them with a 403. The statement stays readable. -->
        @if (!canManualBooking || !canReserveTransfer) {
            <p-message severity="warn" styleClass="w-full mb-3">
                <span>
                    @if (!canManualBooking && !canReserveTransfer) {
                        Der Server hat manuelle Buchungen und Umbuchungen verweigert: Ihnen fehlen
                        die Rechte <strong>cp_manualBooking</strong> und
                        <strong>cp_reserveTransfer</strong>.
                    } @else if (!canManualBooking) {
                        Der Server hat die manuelle Buchung verweigert: Ihnen fehlt das Recht
                        <strong>cp_manualBooking</strong>.
                    } @else {
                        Der Server hat die Umbuchung verweigert: Ihnen fehlt das Recht
                        <strong>cp_reserveTransfer</strong>.
                    }
                    Der Kontoauszug bleibt lesbar.
                </span>
            </p-message>
        }

        <!-- ══════════════════════════════════════════════════════════════════════════════════ -->
        <!-- THE STATEMENT — the four explicit data states (loading / error / empty / ready)    -->
        <!-- ══════════════════════════════════════════════════════════════════════════════════ -->
        @if (busy) {

            <p-skeleton height="20rem"></p-skeleton>

        } @else if (loadFailed) {

            <p-message severity="error" styleClass="w-full mb-3">
                <span>
                    <i class="fa-regular fa-circle-exclamation mr-2"></i>
                    {{ errorMessage }}
                </span>
            </p-message>
            <p-button
                    label="Erneut laden"
                    icon="fa-regular fa-rotate-right"
                    severity="secondary"
                    [outlined]="true"
                    (onClick)="refreshComponent()">
            </p-button>

        } @else if (!transactions.length) {

            <!-- NO Saldovortrag is stated here, and none is computed either.

                 An empty page has no oldest line to compute a balance BEFORE, so the backend
                 answers openingBalance = null — "nicht ermittelt"
                 (ProvisionAgentAccountStatementService.emptyPage). It deliberately does NOT
                 substitute the head balance: the head figure is the balance TODAY, not the balance
                 before a booking-free period, and stating it here would read as
                 "Der Saldo vor dem Zeitraum beträgt 4.321,00 €" — a fact nobody established.
                 So the empty state says only that there are no bookings; the head figure above
                 keeps stating what IS known, and nothing is recomputed here to fill the gap. -->
            <p-message severity="info" styleClass="w-full">
                <span>
                    Für das {{ accountKindLabel }} gibt es im gewählten Zeitraum keine Buchungen.
                </span>
            </p-message>

        } @else {

            <p-table
                    [value]="transactions"
                    [scrollable]="true"
                    styleClass="p-datatable-sm"
                    dataKey="id">

                <ng-template pTemplate="header">
                    <tr>
                        <th class="w-7rem">Valuta</th>
                        <th class="w-7rem">Buchung</th>
                        <th class="w-8rem">Richtung</th>
                        <th>Buchungsart</th>
                        <th class="w-14rem">Ereignis</th>
                        <th class="w-8rem">Status</th>
                        <th class="w-10rem text-right">Betrag</th>
                        <th class="w-10rem text-right">{{ balanceColumnLabel }}</th>
                        <th class="w-6rem text-center">Aktionen</th>
                    </tr>

                    <!-- Saldovortrag, ASCENDING ONLY.
                         The backend computes openingBalance as the balance before the JOURNAL-OLDEST
                         line of a page and reverses the page afterwards for a descending display
                         (ProvisionAgentAccountStatementService). The row therefore belongs directly
                         ABOVE the first line only while the oldest booking IS the first line, i.e.
                         for direction 'asc'. For 'desc' (the default) it is rendered in the table
                         foot below, where the oldest booking sits — otherwise the column would read
                         as "0,00 € / -30,00 / 120,00" and look broken.

                         ACROSS APPENDED PAGES the figure follows the journal-oldest line of the
                         WHOLE list, not of the last page fetched — the component adopts it in
                         `adoptOpeningBalance`. For 'asc' that is the first page, so this row keeps
                         stating the seed the column above it actually continues from. -->
                    @if (direction === 'asc') {
                        <tr class="cp-agent-account-opening">
                            <th colspan="7" class="text-right font-normal text-color-secondary">
                                {{ openingBalanceLabel }}
                            </th>
                            <th class="text-right font-semibold"
                                [ngClass]="amountToneClass(openingBalance)">
                                @if (openingBalance != null) {
                                    {{ openingBalance | currency:'EUR':'symbol':'1.2-2' }}
                                } @else {
                                    —
                                }
                            </th>
                            <th></th>
                        </tr>
                    }
                </ng-template>

                <!-- Saldovortrag, DESCENDING ONLY — the other half of the same rule: newest first
                     means the oldest booking is the LAST line, so the balance it continues from
                     closes the column instead of opening it. -->
                <ng-template pTemplate="footer">
                    @if (direction !== 'asc') {
                        <tr class="cp-agent-account-opening">
                            <td colspan="7" class="text-right font-normal text-color-secondary">
                                {{ openingBalanceLabel }}
                            </td>
                            <td class="text-right font-semibold"
                                [ngClass]="amountToneClass(openingBalance)">
                                @if (openingBalance != null) {
                                    {{ openingBalance | currency:'EUR':'symbol':'1.2-2' }}
                                } @else {
                                    —
                                }
                            </td>
                            <td></td>
                        </tr>
                    }
                </ng-template>

                <ng-template pTemplate="body" let-row>

                    <tr [style.background-color]="row.transactionTypeRgbaColorBackground || null"
                        [class.cp-agent-account-anchor]="isAnchorTransaction(row)"
                        [class.cp-line-through]="row.status === ProvisionTransactionStatus.CANCELLED">

                        <td>{{ row.valutaDate | date:'dd.MM.yyyy' }}</td>

                        <td>{{ row.bookingDate | date:'dd.MM.yyyy' }}</td>

                        <!-- Richtung: the ONLY use of bookingKind — a caption, never a sign. -->
                        <td class="cp-no-line-through">
                            <p-tag [value]="bookingKindLabel(row.bookingKind)" severity="secondary"></p-tag>
                        </td>

                        <td>
                            @if (row.transactionTypeImage) {
                                <i [class]="row.transactionTypeImage + ' mr-2'"
                                   [style.color]="row.transactionTypeRgbaColor || null"></i>
                            }
                            {{ row.transactionTypeName }}
                            @if (row.referenceText) {
                                <div class="text-color-secondary text-xs mt-1">{{ row.referenceText }}</div>
                            }
                        </td>

                        <td>{{ eventLabel(row.event) }}</td>

                        <!-- The status chip stays readable on a struck-through row. -->
                        <td class="cp-no-line-through">
                            <p-tag [value]="statusLabel(row.status)"
                                   [severity]="statusSeverity(row.status)"></p-tag>
                        </td>

                        <!-- Betrag: signedAmount arrives SIGNED — printed and coloured as delivered,
                             through amountToneClass, which reads that sign and nothing else. -->
                        <td class="text-right font-semibold"
                            [ngClass]="amountToneClass(row.signedAmount)">
                            {{ row.signedAmount | currency:'EUR':'symbol':'1.2-2' }}
                        </td>

                        <!-- Saldo: runningBalance arrives SIGNED; a verworfene Buchung repeats the
                             balance of its predecessor (GR-3). -->
                        <td class="text-right font-bold"
                            [ngClass]="amountToneClass(row.runningBalance)">
                            {{ row.runningBalance | currency:'EUR':'symbol':'1.2-2' }}
                        </td>

                        <td class="text-center cp-no-line-through">
                            @if (row.hasContractDetail === true) {
                                <p-button
                                        [icon]="detailLoadingRows[row.id]
                                            ? 'fa-regular fa-spinner fa-spin'
                                            : (isRowExpanded(row.id) ? 'fa-regular fa-chevron-down' : 'fa-regular fa-chevron-right')"
                                        severity="secondary"
                                        [text]="true"
                                        [rounded]="true"
                                        [disabled]="detailLoadingRows[row.id]"
                                        pTooltip="Vertragsbuchung zu dieser Zeile anzeigen"
                                        tooltipPosition="top"
                                        (onClick)="toggleRowExpansion(row)">
                                </p-button>
                            } @else {
                                <span class="text-color-secondary"
                                      pTooltip="Zu dieser Buchung gibt es kein Vertragspendant."
                                      tooltipPosition="top">—</span>
                            }
                        </td>
                    </tr>

                    <!-- ─── Expand row: the linked ProvisionContractTransaction (lazy) ─── -->
                    @if (isRowExpanded(row.id)) {
                        <tr>
                            <td [attr.colspan]="9" class="p-3 surface-50">

                                @if (detailErrors[row.id]) {

                                    <p-message severity="error" styleClass="w-full">
                                        <span>{{ detailErrors[row.id] }}</span>
                                    </p-message>

                                } @else if (detailWithoutContract[row.id]) {

                                    <!-- HTTP 204 — a valid outcome (manuelle Buchung, Auszahlung),
                                         never an error state. -->
                                    <p-message severity="info" styleClass="w-full">
                                        <span>
                                            Zu dieser Buchung gibt es kein Vertragspendant — sie wurde
                                            manuell oder als Auszahlung erfasst.
                                        </span>
                                    </p-message>

                                } @else if (contractDetails[row.id]) {

                                    <div class="grid">

                                        <div class="col-12 md:col-6 lg:col-4">
                                            <div class="surface-card p-3 border-round h-full">
                                                <div class="text-sm font-semibold text-primary mb-2">
                                                    <i class="fa-regular fa-file-contract mr-1"></i> Vertrag
                                                </div>
                                                <div class="flex justify-content-between mb-1 text-sm">
                                                    <span class="text-color-secondary">Vertrag:</span>
                                                    <span class="font-medium">
                                                        {{ contractDetails[row.id].contractAlias || '—' }}
                                                    </span>
                                                </div>
                                                <div class="flex justify-content-between mb-1 text-sm">
                                                    <span class="text-color-secondary">Ereigniszeile:</span>
                                                    <span class="font-medium">
                                                        {{ contractDetails[row.id].provisionContractId ?? '—' }}
                                                    </span>
                                                </div>
                                                <div class="flex justify-content-between text-sm">
                                                    <span class="text-color-secondary">Buchungsart:</span>
                                                    <span class="font-medium">
                                                        {{ contractDetails[row.id].contractTransactionTypeName || '—' }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="col-12 md:col-6 lg:col-4">
                                            <div class="surface-card p-3 border-round h-full">
                                                <div class="text-sm font-semibold text-primary mb-2">
                                                    <i class="fa-regular fa-percent mr-1"></i> Provisionsart
                                                </div>
                                                <div class="flex justify-content-between mb-1 text-sm">
                                                    <span class="text-color-secondary">Provisionsart:</span>
                                                    <span class="font-medium">
                                                        {{ contractDetails[row.id].provisionTypeName || '—' }}
                                                    </span>
                                                </div>
                                                <div class="flex justify-content-between mb-1 text-sm">
                                                    <span class="text-color-secondary">Komponente:</span>
                                                    <span class="font-medium">
                                                        {{ contractDetails[row.id].provisionTypeComponentName || '—' }}
                                                    </span>
                                                </div>
                                                <div class="flex justify-content-between mb-1 text-sm">
                                                    <span class="text-color-secondary">Provisionssatz:</span>
                                                    <span class="font-medium">
                                                        @if (contractDetails[row.id].appliedFactor != null) {
                                                            {{ contractDetails[row.id].appliedFactor | number:'1.2-4' }}
                                                        } @else {
                                                            —
                                                        }
                                                    </span>
                                                </div>
                                                <div class="flex justify-content-between text-sm">
                                                    <span class="text-color-secondary">Rücklagenanteil:</span>
                                                    <span class="font-medium">
                                                        @if (contractDetails[row.id].appliedReservePercentage != null) {
                                                            {{ contractDetails[row.id].appliedReservePercentage | number:'1.2-2' }} %
                                                        } @else {
                                                            —
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="col-12 md:col-12 lg:col-4">
                                            <div class="surface-card p-3 border-round h-full">
                                                <div class="text-sm font-semibold text-primary mb-2">
                                                    <i class="fa-regular fa-receipt mr-1"></i> Vertragsbuchung
                                                </div>
                                                <div class="flex justify-content-between mb-1 text-sm">
                                                    <span class="text-color-secondary">Ereignis:</span>
                                                    <span class="font-medium">
                                                        {{ eventLabel(contractDetails[row.id].event) }}
                                                    </span>
                                                </div>
                                                <div class="flex justify-content-between mb-1 text-sm">
                                                    <span class="text-color-secondary">Status:</span>
                                                    <span class="font-medium">
                                                        {{ statusLabel(contractDetails[row.id].status) }}
                                                    </span>
                                                </div>
                                                <div class="flex justify-content-between mb-1 text-sm">
                                                    <span class="text-color-secondary">Betrag:</span>
                                                    <span class="font-medium">
                                                        {{ contractDetails[row.id].amount | currency:'EUR':'symbol':'1.2-2' }}
                                                    </span>
                                                </div>
                                                <div class="flex justify-content-between mb-1 text-sm">
                                                    <span class="text-color-secondary">Valuta:</span>
                                                    <span class="font-medium">
                                                        {{ contractDetails[row.id].valutaDate | date:'dd.MM.yyyy' }}
                                                    </span>
                                                </div>
                                                <div class="flex justify-content-between text-sm">
                                                    <span class="text-color-secondary">Provisionslauf:</span>
                                                    <span class="font-medium">
                                                        {{ contractDetails[row.id].provisionRunId ?? 'manuell' }}
                                                    </span>
                                                </div>
                                                @if (contractDetails[row.id].referenceText) {
                                                    <div class="text-color-secondary text-xs mt-2">
                                                        {{ contractDetails[row.id].referenceText }}
                                                    </div>
                                                }
                                            </div>
                                        </div>

                                    </div>

                                }

                            </td>
                        </tr>
                    }

                </ng-template>

            </p-table>

            <!-- ══════════════════════════════════════════════════════════════════════════════ -->
            <!-- PAGING — a CURSOR, not a growing page                                          -->
            <!--                                                                                -->
            <!-- The button appends the NEXT page over the EXCLUSIVE nextAnchorTransactionId,    -->
            <!-- which the component sends back verbatim; the pages abut without a repeated and  -->
            <!-- without a skipped booking. `hasMore` is that cursor being non-null — the        -->
            <!-- module's end-of-journal signal and, since the ceiling now sizes ONE page rather  -->
            <!-- than the reach, the only reason the button ever disappears. The sentence that   -->
            <!-- used to stand here ("Es werden maximal 200 Buchungen angezeigt — bitte den      -->
            <!-- Valuta-Zeitraum einschränken") described exactly the defect and is gone with it. -->
            <!-- ══════════════════════════════════════════════════════════════════════════════ -->
            @if (appendFailed) {
                <!-- Beside the table, not instead of it: the lines already loaded were read
                     successfully and their running balances are absolute figures. Only the next
                     page is missing, the cursor stands still, and the button repeats the request. -->
                <p-message severity="error" styleClass="w-full mt-3">
                    <span>
                        <i class="fa-regular fa-circle-exclamation mr-2"></i>
                        {{ appendErrorMessage }}
                    </span>
                </p-message>
            }

            <div class="flex flex-column align-items-center gap-2 mt-3">

                @if (hasMore) {
                    <p-button
                            label="Weitere Buchungen laden"
                            icon="fa-regular fa-chevron-down"
                            severity="secondary"
                            [outlined]="true"
                            [loading]="appending"
                            [disabled]="appending"
                            (onClick)="loadMore()">
                    </p-button>
                } @else {
                    <span class="text-color-secondary text-sm">
                        <i class="fa-regular fa-circle-check mr-1"></i>
                        Alle Buchungen des gewählten Zeitraums sind geladen.
                    </span>
                }

                <!-- What is on screen against what the filter matches — the honest replacement for
                     the removed ceiling notice. `!= null` because 0 is a valid count. -->
                @if (totalTransactionCount != null) {
                    <span class="text-color-secondary text-xs">
                        {{ transactions.length }} von {{ totalTransactionCount }} Buchungen geladen
                    </span>
                }

            </div>

        }

        <!-- Action dialogs: metadata-driven forms, reused unchanged, no hand-written form HTML -->
        <cp-provision-agent-manual-booking-dialog
                [provisionAgentId]="provisionAgentId"
                [(visible)]="showManualBookingDialog"
                (onForbidden)="handleManualBookingForbidden()"
                (onBooked)="handleBookingDone()">
        </cp-provision-agent-manual-booking-dialog>

        <cp-provision-agent-transfer-dialog
                [provisionAgentId]="provisionAgentId"
                [(visible)]="showTransferDialog"
                (onForbidden)="handleReserveTransferForbidden()"
                (onTransferred)="handleBookingDone()">
        </cp-provision-agent-transfer-dialog>

    }

</div>
